import app from 'app';
import _ from 'lodash';

import { parseAddress } from 'helpers/stringParsers';

app.service('LocationService', ($q, GetCare, Raven) => {
  function parseGeocodedAddress(geocodedAddress) {
    const { formatted_address, geometry } = geocodedAddress; // eslint-disable-line camelcase
    return {
      locationQuery: formatted_address,
      latitude: geometry.location.lat(),
      longitude: geometry.location.lng(),
    };
  }

  function getGeocodedAddress(address) {
    if (!address) {
      return $q.reject();
    }

    if (!_.isUndefined(address.latitude) && !_.isUndefined(address.longitude)) {
      const { latitude, longitude } = address;
      return $q.when({
        latitude,
        longitude,
        locationQuery: parseAddress(address, false, ', '),
      });
    }

    if (!address.address) {
      return GetCare.geocode({
        address: parseAddress(address, false, ', '),
      }).then((addresses) => parseGeocodedAddress(addresses[0]));
    }

    return GetCare.geocode(address).then((addresses) =>
      parseGeocodedAddress(addresses[0])
    );
  }

  /**
   * Recursive function for getting the correct geocoded
   * address given a list of addresses that is prioritized
   * @param  {object[]} addresses - prioritized list
   * @param  {number} index
   * @return {promise} resolves to correct geocoded address
   */
  function getGeocodedAddressRecurse(addresses, index) {
    if (index >= addresses.length) {
      return $q.reject();
    }

    return getGeocodedAddress(addresses[index]).catch(() =>
      getGeocodedAddressRecurse(addresses, index + 1)
    );
  }

  /**
   * Returns an ordered list of addresses in the order
   * we should use them. Order is:
   * 1) Preferred
   * 2) HRIS
   * 3) Subscriber
   * 4) Sponsor
   * @param  {object} user
   * @param  {object} sponsor
   * @return {object[]}
   */
  function getPrioritizedAddresses(user, sponsor) {
    return [
      user.preferredAddress,
      _.find(user.addresses, { type: 'preferred' }),
      _.find(user.addresses, { type: 'mailing' }),
      user.mailingAddress,
      sponsor.address,
    ].filter((x) => !!x);
  }

  /**
   * Gets a geocoded address for the user based on the
   * prioritized addresses.
   * @param  {object} user
   * @param  {object} sponsor
   * @return {promise} resolves with correct geocoded address
   */
  function getUsersGeocodedAddress(user, sponsor) {
    return getGeocodedAddressRecurse(
      getPrioritizedAddresses(user.user, sponsor),
      0
    ).catch(() => {
      Raven.captureMessage('Could not geocode address', {
        personId: user.id,
        sponsorCode: sponsor.sponsorCode,
      });
      return $q.reject();
    });
  }

  return {
    getGeocodedAddress,
    getPrioritizedAddresses,
    getUsersGeocodedAddress,
  };
});
